import { useMemo } from 'react'
import { useQuery, type QueryClient } from '@tanstack/react-query'
import type { PlatformModuleId } from '@/types'
import type { Loan, PipelineMetrics, ValuationRecord, DevelopmentFeasibility } from '@/types'
import {
  ANTARCTICA_DEALS,
  MARKET_RATES,
  PIPELINE_METRICS,
  PRODUCT_MIX,
  VALUATION_RECORDS,
  DEVELOPMENT_PROJECTS,
} from '@/data/clientMockData'
import { isAzureAuthEnabled } from '@/auth/authMode'
import { useLocalAuth } from '@/auth/LocalAuthContext'
import { msalInstance } from '@/auth/msalInstance'
import { useDemoDataSettings } from '@/context/DemoDataContext'
import { analyticsBook, asLoanArray, type AnalyticsBook } from '@/pages/liveModuleData'
import { scopeLoans } from '@/pages/pipeline-intelligence/deadScope'
import type { DealScope } from '@/pages/pipeline-intelligence/types'
import { loansService } from '@/services/loans'
import { dashboardsService, type DealExceptionsResponse } from '@/services/dashboards'

export const LIVE_LOANS_QUERY_KEY = ['loans', 'live-book'] as const
/** The dead deals, kept apart from the live book on purpose: nothing that reads
 *  the live book ever sees one. Under the `['loans']` prefix so a kill or a
 *  reopen (`invalidateLoanQueries`) refreshes it with everything else. */
export const DEAD_LOANS_QUERY_KEY = ['loans', 'dead-book'] as const

function hasMsalAccount(): boolean {
  if (!isAzureAuthEnabled || !msalInstance) return false
  return Boolean(msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0])
}

/** Local JWT or an MSAL account — do not fire GET /loans before a bearer exists. */
export function isLiveBookAuthReady(localToken: string | null): boolean {
  if (localToken) return true
  if (!isAzureAuthEnabled) return true
  return hasMsalAccount()
}

/** Attach each deal's credit-box exceptions — the one exception definition
 *  the executive surfaces share — to the live book. A reader who cannot see
 *  the screen (403) or a failed read leaves the field absent, which the grid
 *  treats as "not loaded", never as "no exceptions". */
export function withCreditBoxExceptions(
  loans: Loan[],
  readings: DealExceptionsResponse | null,
): Loan[] {
  if (!readings) return loans
  return loans.map((loan) => {
    const reading = readings.loans[loan.id]
    return {
      ...loan,
      creditBoxExceptions: reading?.exceptions ?? null,
      // What each ratio check was computed on, and the ruleset's sign-off
      // status, so no surface prints a stand-in as the program's own test
      // (exec-03).
      creditBoxBasis: reading?.basis ?? null,
      creditBoxRulesNote: reading ? (reading.rulesNote ?? readings.rulesNote ?? null) : null,
    }
  })
}

async function fetchLiveLoans(): Promise<Loan[]> {
  const [loans, readings] = await Promise.all([
    loansService.getAll(),
    // Never lets the exceptions read fail the book, even by throwing
    // synchronously: the loans are the page, the screen is annotation.
    Promise.resolve()
      .then(() => dashboardsService.getDealExceptions())
      .catch(() => null),
  ])
  return withCreditBoxExceptions(asLoanArray(loans), readings)
}

const EMPTY_VALUATIONS: ValuationRecord[] = []
const EMPTY_DEV: DevelopmentFeasibility[] = []
const EMPTY_METRICS: PipelineMetrics = {
  totalVolume: 0,
  dealCount: 0,
  avgLTV: 0,
  avgDSCR: 0,
  inClosing: 0,
  overdueTriggers: 0,
}
const EMPTY_PRODUCT_MIX: typeof PRODUCT_MIX = []
type MarketRates = { [K in keyof typeof MARKET_RATES]: number }

const EMPTY_MARKET_RATES: MarketRates = {
  sofr: 0,
  ust10y: 0,
  cmbsAaaSpread: 0,
  cmbsBbbSpread: 0,
  multifamilyCapRate: 0,
  officeCapRate: 0,
  industrialCapRate: 0,
  retailCapRate: 0,
  hotelCapRate: 0,
  seniorLivingCapRate: 0,
}

function gate<T>(enabled: boolean, demoValue: T, liveValue: T): T {
  return enabled ? demoValue : liveValue
}

export function useAntarcticaDeals(moduleId: PlatformModuleId) {
  const { isModuleDemoEnabled } = useDemoDataSettings()
  const demoEnabled = isModuleDemoEnabled(moduleId)
  const { localToken } = useLocalAuth()
  const authReady = isLiveBookAuthReady(localToken)
  const { data } = useQuery({
    queryKey: LIVE_LOANS_QUERY_KEY,
    queryFn: fetchLiveLoans,
    enabled: !demoEnabled && authReady,
    staleTime: 60_000,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  })
  return gate(demoEnabled, ANTARCTICA_DEALS, asLoanArray(data))
}

const idleRefetch = async () => undefined

const liveBookQueryOptions = {
  queryKey: LIVE_LOANS_QUERY_KEY,
  queryFn: fetchLiveLoans,
  staleTime: 60_000,
  retry: 3,
  retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 8000),
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
  refetchInterval: (query: { state: { status: string } }) =>
    query.state.status === 'error' ? 15_000 : false,
} as const

/** Fetch live pipeline loans from API when demo is off; otherwise use provided demo set. */
export function useModuleLoans(moduleId: PlatformModuleId, demoLoans: Loan[]) {
  const { isModuleDemoEnabled } = useDemoDataSettings()
  const demoEnabled = isModuleDemoEnabled(moduleId)
  const { localToken } = useLocalAuth()
  const authReady = isLiveBookAuthReady(localToken)
  const query = useQuery({
    ...liveBookQueryOptions,
    enabled: !demoEnabled && authReady,
  })
  if (demoEnabled) {
    return {
      loans: demoLoans,
      isLoading: false,
      isError: false,
      isLive: false,
      refetch: idleRefetch,
    }
  }
  // Stay in loading until a successful payload arrives. A failed GET must not
  // flip the module to an error/retry wall — refetchInterval recovers it.
  const resolved = query.data !== undefined
  return {
    loans: asLoanArray(query.data),
    isLoading: !resolved,
    isError: false,
    isLive: true,
    refetch: query.refetch,
  }
}

const NO_LOANS: Loan[] = []

/** Dead deals only — GET /loans with `killed_only`. The server counts the
 *  kill record, not this screen. */
async function fetchDeadLoans(): Promise<Loan[]> {
  return asLoanArray(await loansService.getAll({ killed_only: 'true' }))
}

/**
 * The deals a scope reads, fetched now rather than rendered from a hook — for a
 * button handler (Reporting's Run Now) that needs the list at the moment of the
 * click. The dead book comes from the same cache `useDeadLoans` fills, so a
 * screen that already read it does not read it twice. A demo account holds no
 * dead deals: it gets none, never a stand-in.
 */
export async function loansForScope(
  queryClient: QueryClient,
  scope: DealScope,
  activeLoans: Loan[],
  options: { demo: boolean },
): Promise<Loan[]> {
  if (scope === 'active') return activeLoans
  const dead = options.demo
    ? NO_LOANS
    : await queryClient.fetchQuery({
        queryKey: DEAD_LOANS_QUERY_KEY,
        queryFn: fetchDeadLoans,
        staleTime: 60_000,
      })
  return scopeLoans(scope, activeLoans, dead)
}

export interface DeadBook {
  loans: Loan[]
  /** The read is in flight and has not answered. */
  isLoading: boolean
  /** The read failed and no earlier answer is cached. */
  isError: boolean
  /** A demo account holds no dead deals: the comparison needs the live book.
   *  Never a stand-in list. */
  needsLiveData: boolean
  refetch: () => Promise<unknown>
}

/**
 * The dead-deal book, read only while a scope that wants it is selected.
 *
 * Unlike the live book, a failed read is reported: an empty list there reads as
 * "no deals", and an empty dead book would read as "nothing was ever killed".
 */
export function useDeadLoans(moduleId: PlatformModuleId, enabled: boolean): DeadBook {
  const { isModuleDemoEnabled } = useDemoDataSettings()
  const demoEnabled = isModuleDemoEnabled(moduleId)
  const { localToken } = useLocalAuth()
  const authReady = isLiveBookAuthReady(localToken)
  const live = enabled && !demoEnabled
  const query = useQuery({
    queryKey: DEAD_LOANS_QUERY_KEY,
    queryFn: fetchDeadLoans,
    enabled: live && authReady,
    staleTime: 60_000,
  })
  const loans = useMemo(() => (live ? asLoanArray(query.data) : NO_LOANS), [live, query.data])
  const resolved = query.data !== undefined
  return {
    loans,
    isLoading: live && !resolved && !query.isError,
    isError: live && !resolved && query.isError,
    needsLiveData: enabled && demoEnabled,
    refetch: query.refetch,
  }
}

/**
 * The deals a scope reads, given the live book a page already holds — for the
 * screens (Reporting) that load the live book their own way and need the dead
 * ones only when a saved view's scope asks for them.
 */
export function useScopedLoans(
  moduleId: PlatformModuleId,
  scope: DealScope,
  activeLoans: Loan[],
): Loan[] {
  const dead = useDeadLoans(moduleId, scope !== 'active')
  return useMemo(() => scopeLoans(scope, activeLoans, dead.loans), [scope, activeLoans, dead.loans])
}

/**
 * The Pipeline grid's book: the live pipeline, the dead deals, or both, by the
 * selected view's scope.
 *
 * `useModuleLoans` stays the live book — same key, same query — so switching
 * back to an active view is instant and nothing about the active path changed.
 * The dead book is a second query that exists only while a scope wants it.
 * Demo accounts never get fabricated dead rows: `needsLiveData` says why.
 */
export function usePipelineLoans(moduleId: PlatformModuleId, demoLoans: Loan[], scope: DealScope) {
  const book = useModuleLoans(moduleId, demoLoans)
  const dead = useDeadLoans(moduleId, scope !== 'active')
  const loans = useMemo(() => scopeLoans(scope, book.loans, dead.loans), [scope, book.loans, dead.loans])
  return {
    loans,
    /** The live book alone (the demo set on a demo account). */
    activeLoans: book.loans,
    /** The dead book alone — empty until read, and always empty on demo. */
    deadLoans: dead.loans,
    scope,
    isLive: book.isLive,
    // The grid waits on what it is about to show: the dead book for 'dead', both
    // for 'all'. A dead read that failed is `deadError`, not a forever-spinner.
    isLoading: scope === 'dead' ? dead.isLoading : book.isLoading || (scope === 'all' && dead.isLoading),
    deadLoading: dead.isLoading,
    deadError: dead.isError,
    needsLiveData: dead.needsLiveData,
    refetchDead: dead.refetch,
    refetch: book.refetch,
  }
}

export interface LiveBookStatus {
  /** The live book has not answered yet (first load, or waiting for sign-in). */
  isLoading: boolean
  /** The live book failed and no earlier answer is cached. */
  isError: boolean
  /** False when the module is showing its demo dataset. */
  isLive: boolean
  error: unknown
  refetch: () => Promise<unknown>
}

/**
 * Whether the deal list `useAntarcticaDeals` returns is an ANSWER yet.
 *
 * `useAntarcticaDeals` returns `[]` while GET /loans is in flight and after it
 * fails, which is indistinguishable from an empty book — so sixteen screens
 * told the user "No deals available … add a deal from Intake" during a slow
 * load, and Screening sat on "SCREENING QUEUE 0" through a 503. This reads the
 * same query (same key, so no second request) and says which of the three
 * states the list is in. `LiveBookGate` renders it.
 */
export function useLiveBookStatus(moduleId: PlatformModuleId): LiveBookStatus {
  const { isModuleDemoEnabled } = useDemoDataSettings()
  const demoEnabled = isModuleDemoEnabled(moduleId)
  const { localToken } = useLocalAuth()
  const authReady = isLiveBookAuthReady(localToken)
  const query = useQuery({
    ...liveBookQueryOptions,
    enabled: !demoEnabled && authReady,
  })
  if (demoEnabled) {
    return { isLoading: false, isError: false, isLive: false, error: null, refetch: idleRefetch }
  }
  const resolved = query.data !== undefined
  return {
    isLoading: !resolved && !query.isError,
    isError: !resolved && query.isError,
    isLive: true,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * The book Portfolio Analytics analyses.
 *
 * Demo: the curated portfolio, as before. Live: FUNDED loans from the live
 * book, normalised to the shape and $MM units the analytics were written for,
 * with loans missing a required input listed rather than filled in.
 */
export function usePortfolioAnalyticsBook(): AnalyticsBook & { isLive: boolean } {
  const { isModuleDemoEnabled } = useDemoDataSettings()
  const demoEnabled = isModuleDemoEnabled('portfolio-analytics')
  const deals = useAntarcticaDeals('portfolio-analytics')
  return useMemo(
    () =>
      demoEnabled
        ? { funded: deals, complete: deals, incomplete: [], pipeline: [], pipelineExcluded: 0, isLive: false }
        : { ...analyticsBook(deals), isLive: true },
    [demoEnabled, deals],
  )
}

export function useMarketRates(moduleId: PlatformModuleId = 'market-intelligence') {
  const { isModuleDemoEnabled } = useDemoDataSettings()
  return gate(isModuleDemoEnabled(moduleId), MARKET_RATES, EMPTY_MARKET_RATES)
}

export function usePipelineMetrics(moduleId: PlatformModuleId = 'reports') {
  const { isModuleDemoEnabled } = useDemoDataSettings()
  const demoEnabled = isModuleDemoEnabled(moduleId)
  const { data } = useQuery({
    queryKey: ['loans', 'metrics'],
    queryFn: () => loansService.getMetrics(),
    enabled: !demoEnabled,
    staleTime: 60_000,
  })
  const liveMetrics: PipelineMetrics = data
    ? { ...EMPTY_METRICS, ...data }
    : EMPTY_METRICS
  return gate(demoEnabled, PIPELINE_METRICS, liveMetrics)
}

export function useProductMix(moduleId: PlatformModuleId = 'reports') {
  const { isModuleDemoEnabled } = useDemoDataSettings()
  return gate(isModuleDemoEnabled(moduleId), PRODUCT_MIX, EMPTY_PRODUCT_MIX)
}

export function useValuationRecords(moduleId: PlatformModuleId = 'valuation') {
  const { isModuleDemoEnabled } = useDemoDataSettings()
  return gate(isModuleDemoEnabled(moduleId), VALUATION_RECORDS, EMPTY_VALUATIONS)
}

export function useDevelopmentProjects(moduleId: PlatformModuleId = 'asset-management') {
  const { isModuleDemoEnabled } = useDemoDataSettings()
  return gate(isModuleDemoEnabled(moduleId), DEVELOPMENT_PROJECTS, EMPTY_DEV)
}

export function useCommandCenterLoans(enabled = true) {
  // Command Center is always the live book — never the curated demo set — so
  // KPIs, charts and the deal table stay representative of the true pipeline.
  const { localToken } = useLocalAuth()
  const authReady = isLiveBookAuthReady(localToken)
  const { data } = useQuery({
    ...liveBookQueryOptions,
    enabled: authReady && enabled,
  })
  return asLoanArray(data)
}

/**
 * Whether a module may render its illustrative dataset.
 *
 * Numeric series are gated by the hooks above, but pages also carry
 * module-scope constants — alert feeds, market tickers, hand-written
 * period-over-period captions — that no data hook touches. Those kept
 * rendering with demo data off, so a client saw "$0" sitting under
 * "+$120M MTD" beside seven alerts about properties they do not own.
 * Pages use this to gate that decorative content too.
 */
export function useModuleDemoEnabled(moduleId: PlatformModuleId): boolean {
  const { isModuleDemoEnabled } = useDemoDataSettings()
  return isModuleDemoEnabled(moduleId)
}
